// public/js/features/distribution/deliveries-page.js
// The tenant's view of its device orders (docs/device-distribution.md "Orders"):
// what was ordered, where it stands, and the carrier's tracking link once it
// has shipped. Read-only - orders are created and moved by the platform on the
// distribution page. Visible to tenant admins and up (viewDeliveries).
import { listDeliveries } from './api.js';
import { statusPill, STATUS_LABEL } from './order-modal.js';
import { can, getCurrentUser } from '../../modules/rbac.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return iso || ''; } };

// The four steps a delivery walks through, drawn as a small progress line so
// the state reads at a glance without a legend.
const STEPS = ['ordered', 'preparing', 'shipped', 'delivered'];
function progress(status) {
  if (status === 'cancelled') return `<div class="small text-muted">Cancelled</div>`;
  const idx = STEPS.indexOf(status);
  return `<div class="d-flex align-items-center gap-1" style="min-width:180px;">${STEPS.map((s, i) => `
    <span class="d-flex align-items-center gap-1" title="${esc(STATUS_LABEL[s])}">
      <span class="rounded-circle d-inline-block" style="width:10px;height:10px;background:${i <= idx ? '#2563eb' : '#e2e8f0'};"></span>
      ${i < STEPS.length - 1 ? `<span style="width:22px;height:2px;background:${i < idx ? '#2563eb' : '#e2e8f0'};"></span>` : ''}
    </span>`).join('')}</div>`;
}

export async function initDeliveriesPage(container) {
  const groups = getCurrentUser().groups || [];
  if (!can(groups, 'viewDeliveries')) {
    container.innerHTML = `<div class="alert alert-danger m-3">Access denied.</div>`;
    return;
  }
  container.innerHTML = `
    <div class="container-fluid py-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0"><i class="fa-solid fa-truck-fast me-2"></i>Deliveries</h1>
        <button id="dlv-refresh" class="btn btn-sm btn-outline-secondary"><i class="fa-solid fa-rotate me-1"></i>Refresh</button>
      </div>
      <div id="dlv-body"><div class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div></div>
    </div>`;
  container.querySelector('#dlv-refresh').addEventListener('click', () => load(container));
  await load(container);
}

async function load(container) {
  const body = container.querySelector('#dlv-body');
  let orders;
  try { orders = await listDeliveries(); }
  catch { body.innerHTML = `<div class="alert alert-danger">Could not load deliveries.</div>`; return; }

  if (!orders.length) {
    body.innerHTML = `
      <div class="card shadow-sm"><div class="card-body text-center text-muted py-5">
        <i class="fa-solid fa-box-open fa-2x mb-3 d-block"></i>
        <div class="fw-semibold">No deliveries yet</div>
        <div class="small">Orders for your trackers and sensors will appear here with their tracking.</div>
      </div></div>`;
    return;
  }

  const showTenant = new Set(orders.map(o => o.tenantId)).size > 1;
  const cards = orders.map(o => {
    const a = o.address || {};
    const shippedAt = o.tracking?.shippedAt || (o.history || []).find(h => h.status === 'shipped')?.at;
    return `
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <div class="d-flex align-items-center gap-2">
                <span class="h6 mb-0 fw-semibold">${esc(o.orderNo)}</span>${statusPill(o.status)}
                ${showTenant ? `<span class="badge badge-soft-gray">${esc(o.tenantName)}</span>` : ''}
              </div>
              <div class="small text-muted mt-1">Ordered ${fmtDate(o.createdAt)}${shippedAt ? ` · shipped ${fmtDate(shippedAt)}` : ''}${a.name ? ` · to ${esc(a.name)}, ${esc(a.city || '')} ${esc(a.country || '')}` : ''}</div>
            </div>
            ${progress(o.status)}
          </div>
          <div class="mt-2">${(o.lines || []).map(l => `<span class="badge text-bg-light border me-1"><span class="fw-semibold">${l.qty}×</span> ${esc(l.model)}</span>`).join('')}</div>
          ${o.tracking?.number ? `
          <div class="mt-2">
            <a href="${esc(o.tracking.url || '#')}" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">
              <i class="fa-solid fa-truck-fast me-1"></i>Track with ${esc(o.tracking.carrier || 'the carrier')} <span class="font-monospace ms-1">${esc(o.tracking.number)}</span>
            </a>
          </div>` : ''}
          ${(o.devices || []).length ? `<div class="small text-muted mt-2">${o.devices.length} unit(s): <span class="font-monospace">${o.devices.map(d => esc(d.serial || d.deviceId)).join(', ')}</span></div>` : ''}
        </div>
      </div>`;
  }).join('');
  body.innerHTML = cards;
}
